import { useId, useRef, useState, type FormEvent } from 'react';
import { Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, ApiError } from '@/lib/api';
import { friendlyApiError } from '@/lib/apiErrors';
import { usePolledData } from '@/lib/hooks/usePolledData';
import type { ReservedSubdomain } from '@/types/domain';

const fetchReserved = () => api.reservedSubdomains.list();

const RESERVE_ERRORS = {
  INVALID_SUBDOMAIN: (message: string) => `Invalid label: ${message.replace(/^invalid subdomain:\s*/i, '')}`,
};

/** Settings → Reserved Subdomains: labels custom subdomains may not take as-is. */
export function ReservedSubdomainsPanel() {
  const reserved = usePolledData(fetchReserved);
  const ids = useId();
  const [label, setLabel] = useState('');
  const [adding, setAdding] = useState(false);
  // `deleting` keeps the target through the dialog's exit animation; `deleteOpen` controls visibility.
  const [deleting, setDeleting] = useState<ReservedSubdomain | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteBusyRef = useRef(false);

  const rows = reserved.data ?? [];
  const { refetch } = reserved;

  const reserve = async (e: FormEvent) => {
    e.preventDefault();
    const value = label.trim();
    if (value === '' || adding) return;
    setAdding(true);
    try {
      const created = await api.reservedSubdomains.create(value);
      toast.success(`${created.label} reserved`);
      setLabel('');
      refetch();
    } catch (err) {
      toast.error(friendlyApiError(err, RESERVE_ERRORS, 'Failed to reserve the label'));
    } finally {
      setAdding(false);
    }
  };

  const runDelete = async () => {
    const target = deleting;
    if (!target || deleteBusyRef.current) return;
    deleteBusyRef.current = true;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.reservedSubdomains.delete(target.id);
      setDeleteOpen(false);
      toast.success(`${target.label} is no longer reserved`);
      refetch();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Already removed elsewhere: nothing left to confirm.
        setDeleteOpen(false);
        toast.info(`${target.label} was already removed`);
      } else {
        setDeleteError(friendlyApiError(err, {}, 'Failed to remove the label'));
      }
      refetch();
    } finally {
      deleteBusyRef.current = false;
      setDeleteBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Reserved Subdomains</CardTitle>
          <CardDescription>
            Custom subdomains on these labels are never used as-is; the app gets{' '}
            <code className="font-mono">&lt;label&gt;-&lt;deployment id&gt;</code> instead. Removing a label does not affect running apps.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={(e) => void reserve(e)} className="flex flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1 space-y-2">
              <Label htmlFor={`${ids}-label`}>Label</Label>
              <Input
                id={`${ids}-label`}
                value={label}
                onChange={(e) => setLabel(e.target.value.toLowerCase())}
                maxLength={63}
                spellCheck={false}
                autoCapitalize="none"
                autoComplete="off"
                placeholder="admin"
                className="font-mono text-sm"
              />
            </div>
            <Button type="submit" disabled={adding || label.trim() === ''}>
              {adding ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              Reserve
            </Button>
          </form>

          {reserved.loading ? (
            <p role="status" className="py-8 text-center text-muted-foreground">
              Loading reserved subdomains…
            </p>
          ) : reserved.data === undefined ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <span>{reserved.error ?? 'Failed to load reserved subdomains'}</span>
              <Button size="sm" variant="outline" onClick={refetch}>
                Retry
              </Button>
            </div>
          ) : rows.length === 0 ? (
            <EmptyState as="h3" icon={Lock} title="No reserved subdomains" description="Any free label can be used as a custom subdomain." />
          ) : (
            <>
              {reserved.error ? (
                <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                  Could not refresh: {reserved.error}
                </p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Label</TableHead>
                    <TableHead>Reserved</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-sm [overflow-wrap:anywhere]">{r.label}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="destructive"
                          aria-label={`Remove ${r.label}`}
                          onClick={() => {
                            setDeleteError(null);
                            setDeleting(r);
                            setDeleteOpen(true);
                          }}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={(next) => {
          if (!next && !deleteBusyRef.current) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
        title="Remove reserved label?"
        description={deleting ? `New apps will be able to use ${deleting.label} as a subdomain. Running apps are not affected.` : undefined}
        confirmLabel={deleteBusy ? 'Removing…' : 'Remove'}
        destructive
        loading={deleteBusy}
        error={deleteError}
        onConfirm={() => void runDelete()}
      />
    </div>
  );
}
